import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Filter, MoreHorizontal, Mail, Phone, 
  Building2, Trash2, Edit, Eye, Battery, Zap, Sun, MapPin,
  X, ChevronDown, ChevronUp, SlidersHorizontal, RotateCcw, FileText, Sparkles, Download, CheckSquare, Calendar as CalendarIcon,
  Camera, Image, ShoppingBag, Copy, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format, parse } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from '../components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { customerAPI, propertyAPI, installationAPI, documentAPI } from '../lib/api';
import { formatPhoneNumber, getErrorMessage } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';
import AddressAutocomplete from '../components/AddressAutocomplete';

const CustomerForm = ({ customer, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState(customer || {
    first_name: '',
    last_name: '',
    salutation: '',
    company_name: '',
    is_business: false,
    attn: '',
    billing_street: '',
    billing_postal_code: '',
    billing_city: '',
    billing_country: 'België',
    email: '',
    additional_emails: [],
    phone: '',
    language: 'NL',
    vat_number: '',
    sales_person: '',
    notes: '',
    prop_street: '',
    prop_number: '',
    prop_postal_code: '',
    prop_city: ''
  });
  const [hasBillingAddress, setHasBillingAddress] = useState(!!(customer?.billing_street));
  const [kboLoading, setKboLoading] = useState(false);

  const handleKboLookup = async () => {
    setKboLoading(true);
    try {
      const res = await api.get('/lookup/vat', { params: { vat: formData.vat_number } });
      const d = res.data;
      setFormData(prev => ({
        ...prev,
        vat_number: d.vat || prev.vat_number,
        company_name: d.company_name || prev.company_name,
        billing_street: d.street || prev.billing_street,
        billing_postal_code: d.postal_code || prev.billing_postal_code,
        billing_city: d.city || prev.billing_city,
      }));
      if (d.street) setHasBillingAddress(true);
      toast.success(`Bedrijfsgegevens opgehaald: ${d.company_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'BTW-nummer niet gevonden in KBO/VIES');
    } finally {
      setKboLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const additionalEmails = formData.additional_emails || [];

  const updateAdditionalEmail = (index, value) => {
    const arr = [...additionalEmails];
    arr[index] = value;
    handleChange('additional_emails', arr);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      additional_emails: additionalEmails.map(e => e.trim()).filter(e => e && e.includes('@')),
      ...(hasBillingAddress ? {} : { billing_street: '', billing_postal_code: '', billing_city: '', billing_country: '' })
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="space-y-2">
        <Label>Aanspreking</Label>
        <div className="flex gap-2">
          {[['dhr', 'Dhr.'], ['mevr', 'Mevr.']].map(([val, lbl]) => (
            <Button
              key={val}
              type="button"
              size="sm"
              variant={formData.salutation === val ? 'default' : 'outline'}
              onClick={() => handleChange('salutation', formData.salutation === val ? '' : val)}
              data-testid={`customer-salutation-${val}`}
            >
              {lbl}
            </Button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">Voornaam *</Label>
          <Input
            id="first_name"
            value={formData.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            required
            data-testid="customer-first-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name">Naam *</Label>
          <Input
            id="last_name"
            value={formData.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
            required
            data-testid="customer-last-name"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="company_name">Bedrijf</Label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox
                checked={!!formData.is_business}
                onCheckedChange={(v) => handleChange('is_business', !!v)}
                data-testid="customer-is-business"
              />
              <span className="text-xs font-medium">B2B klant</span>
            </label>
          </div>
          <Input
            id="company_name"
            value={formData.company_name || ''}
            onChange={(e) => handleChange('company_name', e.target.value)}
            data-testid="customer-company"
          />
          {formData.is_business && (
            <p className="text-xs text-muted-foreground">Op offertes/facturen: eerst bedrijfsnaam, daarna "T.a.v. {formData.attn || `${formData.first_name} ${formData.last_name}`}"</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="language">Taal</Label>
          <Select value={formData.language || 'NL'} onValueChange={(v) => handleChange('language', v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NL">Nederlands</SelectItem>
              <SelectItem value="FR">Frans</SelectItem>
              <SelectItem value="EN">Engels</SelectItem>
              <SelectItem value="DE">Duits</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {(formData.is_business || formData.company_name) && (
        <div className="space-y-2">
          <Label htmlFor="attn">T.a.v. (contactpersoon op offerte/factuur)</Label>
          <Input
            id="attn"
            value={formData.attn || ''}
            onChange={(e) => handleChange('attn', e.target.value)}
            placeholder={`${formData.first_name} ${formData.last_name}`.trim() || 'Contactpersoon'}
            data-testid="customer-attn"
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            required
            data-testid="customer-email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Telefoonnummer *</Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            required
            data-testid="customer-phone"
          />
        </div>
      </div>

      {/* Extra e-mailadressen (koppels / meerdere contactpersonen) */}
      <div className="space-y-2" data-testid="customer-additional-emails">
        <Label>Extra e-mailadressen</Label>
        {additionalEmails.map((em, idx) => (
          <div key={idx} className="flex gap-2">
            <Input
              type="email"
              value={em}
              onChange={(e) => updateAdditionalEmail(idx, e.target.value)}
              placeholder="extra@voorbeeld.be"
              data-testid={`customer-extra-email-${idx}`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => handleChange('additional_emails', additionalEmails.filter((_, i) => i !== idx))}
              title="Verwijderen"
            >
              <X className="w-4 h-4 text-destructive" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => handleChange('additional_emails', [...additionalEmails, ''])}
          data-testid="customer-add-extra-email"
        >
          <Plus className="w-4 h-4 mr-1" />
          E-mailadres toevoegen
        </Button>
        <p className="text-xs text-muted-foreground">Voor koppels of bedrijven met meerdere contactpersonen — mails kunnen naar alle adressen gestuurd worden.</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="vat_number">BTW Nummer</Label>
          <div className="flex gap-2">
            <Input
              id="vat_number"
              value={formData.vat_number || ''}
              onChange={(e) => handleChange('vat_number', e.target.value)}
              placeholder="BE0123456789"
              data-testid="customer-vat"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleKboLookup}
              disabled={kboLoading || !(formData.vat_number || '').trim()}
              title="Bedrijfsgegevens ophalen uit KBO/VIES"
              data-testid="kbo-lookup-btn"
            >
              {kboLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'KBO'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Klik op KBO om officiële bedrijfsgegevens op te halen</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="sales_person">Verkoper</Label>
          <Input
            id="sales_person"
            value={formData.sales_person || ''}
            onChange={(e) => handleChange('sales_person', e.target.value)}
            data-testid="customer-sales"
          />
        </div>
      </div>

      {!customer && (
        <div className="space-y-2 rounded-lg border border-border/50 p-3" data-testid="customer-property-section">
          <p className="text-sm font-medium">Projectadres <span className="text-muted-foreground font-normal">(optioneel — wordt direct als adres aangemaakt)</span></p>
          <div className="grid grid-cols-2 gap-4 pt-1">
            <div className="space-y-2 col-span-2 sm:col-span-1">
              <Label>Straat</Label>
              <AddressAutocomplete
                value={formData.prop_street}
                onChange={(v) => handleChange('prop_street', v)}
                onSelect={(s) => setFormData(prev => ({
                  ...prev,
                  prop_street: s.street || prev.prop_street,
                  prop_number: s.number || prev.prop_number,
                  prop_postal_code: s.postal_code || prev.prop_postal_code,
                  prop_city: s.city || prev.prop_city,
                }))}
                placeholder="Begin te typen..."
                testId="customer-prop-street"
              />
            </div>
            <div className="space-y-2">
              <Label>Nr.</Label>
              <Input value={formData.prop_number || ''} onChange={(e) => handleChange('prop_number', e.target.value)} data-testid="customer-prop-number" />
            </div>
            <div className="space-y-2">
              <Label>Postcode</Label>
              <Input value={formData.prop_postal_code || ''} onChange={(e) => handleChange('prop_postal_code', e.target.value)} data-testid="customer-prop-postal" />
            </div>
            <div className="space-y-2">
              <Label>Gemeente</Label>
              <Input value={formData.prop_city || ''} onChange={(e) => handleChange('prop_city', e.target.value)} data-testid="customer-prop-city" />
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2 rounded-lg border border-border/50 p-3" data-testid="customer-billing-section">
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={hasBillingAddress}
            onCheckedChange={(v) => {
              setHasBillingAddress(!!v);
              if (!v) {
                setFormData(prev => ({ ...prev, billing_street: '', billing_postal_code: '', billing_city: '', billing_country: '' }));
              } else if (!formData.billing_country) {
                handleChange('billing_country', 'België');
              }
            }}
            data-testid="customer-has-billing-address"
          />
          <span className="text-sm font-medium">Facturatieadres wijkt af van projectadres</span>
        </label>
        {hasBillingAddress && (
          <div className="grid grid-cols-2 gap-4 pt-1">
            <div className="space-y-2 col-span-2">
              <Label htmlFor="billing_street">Straat + nr</Label>
              <Input
                id="billing_street"
                value={formData.billing_street || ''}
                onChange={(e) => handleChange('billing_street', e.target.value)}
                data-testid="customer-billing-street"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="billing_postal_code">Postcode</Label>
              <Input
                id="billing_postal_code"
                value={formData.billing_postal_code || ''}
                onChange={(e) => handleChange('billing_postal_code', e.target.value)}
                data-testid="customer-billing-postal"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="billing_city">Gemeente</Label>
              <Input
                id="billing_city"
                value={formData.billing_city || ''}
                onChange={(e) => handleChange('billing_city', e.target.value)}
                data-testid="customer-billing-city"
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label htmlFor="billing_country">Land</Label>
              <Input
                id="billing_country"
                value={formData.billing_country || ''}
                onChange={(e) => handleChange('billing_country', e.target.value)}
                placeholder="België"
                data-testid="customer-billing-country"
              />
            </div>
            <p className="text-xs text-muted-foreground col-span-2">Indien ingevuld wordt dit adres gebruikt op offertes en facturen i.p.v. het projectadres.</p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notities</Label>
        <Textarea
          id="notes"
          value={formData.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={2}
          data-testid="customer-notes"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuleren
        </Button>
        <Button type="submit" disabled={loading} data-testid="customer-submit">
          {loading ? 'Opslaan...' : (customer ? 'Bijwerken' : 'Aanmaken')}
        </Button>
      </DialogFooter>
    </form>
  );
};

// Filter Section Component
const FilterSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-muted/50 rounded-lg transition-colors">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary" />
          <span className="font-medium text-sm">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(50);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  // Bulk selection state
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  // Camera/Photo state
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [cameraCustomer, setCameraCustomer] = useState(null);
  const [customerPhotos, setCustomerPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [viewingPhotos, setViewingPhotos] = useState(false);
  const fileInputRef = useRef(null);
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  // Default filter state
  const defaultFilters = {
    // Installaties
    hasSolarPanels: null,
    hasBattery: null,
    hasEVCharger: null,
    hasInverter: null,
    // Zonnepanelen range
    minPanels: 0,
    maxPanels: 100,
    // Taal
    languages: [],
    // Gemeente
    cities: [],
    // Postcode
    postalCodes: [],
    // Bedrijf
    hasCompany: null,
    // Reinigingscontract
    cleaningContract: null, // null = all, '2_yearly', '3_yearly', 'none'
    // Installatie datum
    installationDateFrom: '',
    installationDateTo: '',
    // Purchased products filter
    purchasedProducts: [],
    // Sortering
    sortBy: 'name',
    sortOrder: 'asc'
  };

  // Load filters from sessionStorage on mount
  const getInitialFilters = () => {
    try {
      const saved = sessionStorage.getItem('customersFilters');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading filters from session:', e);
    }
    return defaultFilters;
  };

  // Filter states - initialized from sessionStorage
  const [filters, setFilters] = useState(getInitialFilters);

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem('customersFilters', JSON.stringify(filters));
    } catch (e) {
      console.error('Error saving filters to session:', e);
    }
  }, [filters]);

  // Get unique values for filter options
  const filterOptions = useMemo(() => {
    const uniqueCities = [...new Set(properties.map(p => p.city).filter(Boolean))].sort();
    const uniquePostalCodes = [...new Set(properties.map(p => p.postal_code).filter(Boolean))].sort();
    const uniqueLanguages = [...new Set(customers.map(c => c.language).filter(Boolean))];
    
    // Calculate panel counts per customer
    const customerPanelCounts = {};
    installations.forEach(inst => {
      if (inst.installation_type === 'solar_panels' && inst.number_of_panels) {
        const prop = properties.find(p => p.id === inst.property_id);
        if (prop?.customer_id) {
          customerPanelCounts[prop.customer_id] = (customerPanelCounts[prop.customer_id] || 0) + inst.number_of_panels;
        }
      }
    });
    
    const maxPanelCount = Math.max(...Object.values(customerPanelCounts), 100);
    
    // Get unique purchased products across all properties
    const allPurchasedProducts = [];
    const customerPurchasedProducts = {}; // customer_id -> [product_names]
    
    properties.forEach(prop => {
      if (prop.purchased_products && prop.purchased_products.length > 0) {
        prop.purchased_products.forEach(product => {
          // Use 'name' field (not 'product_name')
          const productName = product.name || product.product_name;
          if (productName) {
            allPurchasedProducts.push(productName);
            
            if (prop.customer_id) {
              if (!customerPurchasedProducts[prop.customer_id]) {
                customerPurchasedProducts[prop.customer_id] = [];
              }
              customerPurchasedProducts[prop.customer_id].push(productName);
            }
          }
        });
      }
    });
    
    const uniquePurchasedProducts = [...new Set(allPurchasedProducts)].sort();
    
    return {
      cities: uniqueCities,
      postalCodes: uniquePostalCodes,
      languages: uniqueLanguages,
      maxPanelCount,
      customerPanelCounts,
      purchasedProducts: uniquePurchasedProducts,
      customerPurchasedProducts
    };
  }, [customers, properties, installations]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.hasSolarPanels !== null) count++;
    if (filters.hasBattery !== null) count++;
    if (filters.hasEVCharger !== null) count++;
    if (filters.hasInverter !== null) count++;
    if (filters.minPanels > 0 || filters.maxPanels < 100) count++;
    if (filters.languages.length > 0) count++;
    if (filters.cities.length > 0) count++;
    if (filters.postalCodes.length > 0) count++;
    if (filters.hasCompany !== null) count++;
    if (filters.cleaningContract !== null) count++;
    if (filters.installationDateFrom || filters.installationDateTo) count++;
    if (filters.purchasedProducts?.length > 0) count++;
    return count;
  }, [filters]);

  const fetchData = async () => {
    try {
      const [customersRes, propertiesRes, installationsRes] = await Promise.all([
        customerAPI.getAll(),
        propertyAPI.getAll(),
        installationAPI.getAll()
      ]);
      setCustomers(customersRes.data);
      setProperties(propertiesRes.data);
      setInstallations(installationsRes.data);
    } catch (error) {
      toast.error('Kon gegevens niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Camera/Photo functions
  const openCameraDialog = async (customer) => {
    setCameraCustomer(customer);
    setCameraDialogOpen(true);
    setViewingPhotos(false);
    // Fetch existing photos for this customer
    try {
      const res = await documentAPI.getAll({ customer_id: customer.id, type: 'photo' });
      setCustomerPhotos(res.data || []);
    } catch (error) {
      setCustomerPhotos([]);
    }
  };

  const handlePhotoCapture = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !cameraCustomer) return;

    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('customer_id', cameraCustomer.id);
      formData.append('type', 'photo');
      formData.append('description', `Foto - ${cameraCustomer.first_name} ${cameraCustomer.last_name}`);
      
      await documentAPI.upload(formData);
      toast.success('Foto toegevoegd!');
      
      // Refresh photos
      const res = await documentAPI.getAll({ customer_id: cameraCustomer.id, type: 'photo' });
      setCustomerPhotos(res.data || []);
      setViewingPhotos(true);
    } catch (error) {
      toast.error('Kon foto niet uploaden');
    } finally {
      setPhotoUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const deletePhoto = async (photoId) => {
    try {
      await documentAPI.delete(photoId);
      toast.success('Foto verwijderd');
      setCustomerPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (error) {
      toast.error('Kon foto niet verwijderen');
    }
  };

  // Filter and sort customers
  const filteredCustomers = useMemo(() => {
    let result = [...customers];

    // Text search
    if (search) {
      const searchLower = search.toLowerCase().trim();
      // Only filter if search term is at least 3 characters or is a phone number
      if (searchLower.length >= 3 || /^\d+$/.test(searchLower)) {
        // Split search into words for better matching
        const searchWords = searchLower.split(/\s+/).filter(w => w.length > 0);
        
        // Adresgegevens per klant opbouwen zodat er ook op adres gezocht kan worden
        const addrByCustomer = new Map();
        properties.forEach(p => {
          const t = `${p.street || ''} ${p.number || ''} ${p.postal_code || ''} ${p.city || ''}`.toLowerCase();
          addrByCustomer.set(p.customer_id, `${addrByCustomer.get(p.customer_id) || ''} ${t}`);
        });
        
        result = result.filter(c => {
          // Build searchable text from customer fields
          const fullName = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
          const reverseFullName = `${c.last_name || ''} ${c.first_name || ''}`.toLowerCase();
          const email = (c.email || '').toLowerCase();
          const phone = c.phone || '';
          const company = (c.company_name || '').toLowerCase();
          const address = addrByCustomer.get(c.id) || '';
          
          // Check if all search words match somewhere
          return searchWords.every(word => 
            fullName.includes(word) ||
            reverseFullName.includes(word) ||
            email.includes(word) ||
            phone.includes(word) ||
            company.includes(word) ||
            address.includes(word) ||
            (c.first_name || '').toLowerCase().includes(word) ||
            (c.last_name || '').toLowerCase().includes(word)
          );
        });
      }
    }

    // Get customer installation info
    const getCustomerInstallations = (customerId) => {
      const customerProperties = properties.filter(p => p.customer_id === customerId);
      const propertyIds = customerProperties.map(p => p.id);
      return installations.filter(i => propertyIds.includes(i.property_id));
    };

    // Installation type filters
    if (filters.hasSolarPanels !== null) {
      result = result.filter(c => {
        const custInst = getCustomerInstallations(c.id);
        const has = custInst.some(i => i.installation_type === 'solar_panels');
        return filters.hasSolarPanels ? has : !has;
      });
    }

    if (filters.hasBattery !== null) {
      result = result.filter(c => {
        const custInst = getCustomerInstallations(c.id);
        const has = custInst.some(i => i.installation_type === 'battery');
        return filters.hasBattery ? has : !has;
      });
    }

    if (filters.hasEVCharger !== null) {
      result = result.filter(c => {
        const custInst = getCustomerInstallations(c.id);
        const has = custInst.some(i => i.installation_type === 'ev_charger');
        return filters.hasEVCharger ? has : !has;
      });
    }

    if (filters.hasInverter !== null) {
      result = result.filter(c => {
        const custInst = getCustomerInstallations(c.id);
        const has = custInst.some(i => i.installation_type === 'inverter');
        return filters.hasInverter ? has : !has;
      });
    }

    // Panel count filter
    if (filters.minPanels > 0 || filters.maxPanels < 100) {
      result = result.filter(c => {
        const count = filterOptions.customerPanelCounts[c.id] || 0;
        return count >= filters.minPanels && count <= filters.maxPanels;
      });
    }

    // Language filter
    if (filters.languages.length > 0) {
      result = result.filter(c => filters.languages.includes(c.language));
    }

    // City filter
    if (filters.cities.length > 0) {
      result = result.filter(c => {
        const customerProperties = properties.filter(p => p.customer_id === c.id);
        return customerProperties.some(p => filters.cities.includes(p.city));
      });
    }

    // Postal code filter
    if (filters.postalCodes.length > 0) {
      result = result.filter(c => {
        const customerProperties = properties.filter(p => p.customer_id === c.id);
        return customerProperties.some(p => filters.postalCodes.includes(p.postal_code));
      });
    }

    // Company filter
    if (filters.hasCompany !== null) {
      result = result.filter(c => {
        const has = c.company_name && c.company_name.trim() !== '';
        return filters.hasCompany ? has : !has;
      });
    }

    // Cleaning contract filter
    if (filters.cleaningContract !== null) {
      result = result.filter(c => {
        const customerProperties = properties.filter(p => p.customer_id === c.id);
        if (filters.cleaningContract === 'none') {
          // Klanten zonder contract
          return customerProperties.every(p => !p.cleaning_contract);
        } else {
          // Klanten met specifiek contract type
          return customerProperties.some(p => p.cleaning_contract === filters.cleaningContract);
        }
      });
    }

    // Installation date filter
    if (filters.installationDateFrom || filters.installationDateTo) {
      result = result.filter(c => {
        const customerProperties = properties.filter(p => p.customer_id === c.id);
        const propertyIds = customerProperties.map(p => p.id);
        const customerInstallations = installations.filter(i => propertyIds.includes(i.property_id));
        
        // Check if any installation date falls within the range
        return customerInstallations.some(inst => {
          if (!inst.installation_date) return false;
          const instDate = inst.installation_date.split('T')[0]; // Get date part only
          
          if (filters.installationDateFrom && instDate < filters.installationDateFrom) {
            return false;
          }
          if (filters.installationDateTo && instDate > filters.installationDateTo) {
            return false;
          }
          return true;
        });
      });
    }

    // Purchased products filter (targeted marketing)
    if (filters.purchasedProducts && filters.purchasedProducts.length > 0) {
      result = result.filter(c => {
        // Get all products purchased by this customer
        const customerProducts = filterOptions.customerPurchasedProducts[c.id] || [];
        
        // Check if customer has ANY of the selected products
        return filters.purchasedProducts.some(selectedProduct => 
          customerProducts.includes(selectedProduct)
        );
      });
    }

    // Sorting
    result.sort((a, b) => {
      let comparison = 0;
      switch (filters.sortBy) {
        case 'name':
          comparison = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
          break;
        case 'email':
          comparison = (a.email || '').localeCompare(b.email || '');
          break;
        case 'company':
          comparison = (a.company_name || '').localeCompare(b.company_name || '');
          break;
        case 'panels':
          const aPanels = filterOptions.customerPanelCounts[a.id] || 0;
          const bPanels = filterOptions.customerPanelCounts[b.id] || 0;
          comparison = aPanels - bPanels;
          break;
        default:
          comparison = 0;
      }
      return filters.sortOrder === 'desc' ? -comparison : comparison;
    });

    // Recent bekeken klanten bovenaan (zonder actieve zoekopdracht)
    if (!search) {
      try {
        const recent = JSON.parse(localStorage.getItem('recent_customers') || '[]');
        if (recent.length) {
          const pos = new Map(recent.map((rid, i) => [rid, i]));
          result.sort((a, b) => (pos.has(a.id) ? pos.get(a.id) : 999) - (pos.has(b.id) ? pos.get(b.id) : 999));
        }
      } catch { /* noop */ }
    }
    return result;
  }, [customers, properties, installations, search, filters, filterOptions]);

  // Displayed customers (with limit applied)
  const displayedCustomers = filteredCustomers.slice(0, displayLimit);

  const createPropertyForCustomer = async (customerId, data) => {
    if (!customerId || !data.prop_street?.trim()) return;
    try {
      await propertyAPI.create({
        customer_id: customerId,
        street: data.prop_street.trim(),
        number: (data.prop_number || '').trim(),
        postal_code: (data.prop_postal_code || '').trim(),
        city: (data.prop_city || '').trim(),
      });
    } catch {
      toast.error('Klant aangemaakt, maar het adres kon niet worden opgeslagen');
    }
  };

  const handleCreate = async (data) => {
    setSaving(true);
    try {
      const res = await customerAPI.create(data);
      await createPropertyForCustomer(res.data?.id, data);
      toast.success(data.prop_street?.trim() ? 'Klant + adres aangemaakt' : 'Klant aangemaakt');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      if (error.response?.status === 409) {
        const detail = error.response?.data?.detail || 'Deze klant bestaat al';
        if (window.confirm(`${detail}.\n\nWil je toch een nieuwe (dubbele) klant aanmaken?`)) {
          try {
            const res = await customerAPI.create({ ...data, allow_duplicate: true });
            await createPropertyForCustomer(res.data?.id, data);
            toast.success('Klant aangemaakt (duplicaat toegestaan)');
            setDialogOpen(false);
            fetchData();
          } catch (e2) {
            toast.error(getErrorMessage(e2, 'Kon klant niet aanmaken'));
          }
        }
      } else {
        toast.error(getErrorMessage(error, 'Kon klant niet aanmaken'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (data) => {
    setSaving(true);
    try {
      await customerAPI.update(editCustomer.id, data);
      toast.success('Klant bijgewerkt');
      setEditCustomer(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Kon klant niet bijwerken'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    // Find customer name for confirmation dialog
    const customer = customers.find(c => c.id === id);
    setCustomerToDelete({ id, name: customer ? `${customer.first_name} ${customer.last_name}` : 'deze klant' });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!customerToDelete) return;
    
    try {
      await customerAPI.delete(customerToDelete.id);
      toast.success('Klant verwijderd');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Kon klant niet verwijderen');
    } finally {
      setDeleteConfirmOpen(false);
      setCustomerToDelete(null);
    }
  };

  const handleOfferteReiniging = async (customer) => {
    try {
      toast.loading('Offerte aanvragen...', { id: 'offerte' });
      
      // Call backend endpoint which will forward to webhook
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/offerte-reiniging`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ customer_id: customer.id })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Offerte aanvraag verzonden naar n8n!', { id: 'offerte' });
      } else if (data.message && data.message.includes('404')) {
        toast.error('n8n workflow niet actief - start eerst de workflow', { id: 'offerte' });
      } else {
        throw new Error(data.message || 'Webhook error');
      }
    } catch (error) {
      console.error('Offerte error:', error);
      toast.error('Kon offerte aanvraag niet verzenden', { id: 'offerte' });
    }
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    sessionStorage.removeItem('customersFilters');
  };

  const toggleArrayFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter(v => v !== value)
        : [...prev[key], value]
    }));
  };

  // Bulk selection handlers
  const toggleCustomerSelection = (customerId) => {
    setSelectedCustomers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(customerId)) {
        newSet.delete(customerId);
      } else {
        newSet.add(customerId);
      }
      return newSet;
    });
  };

  const selectAllVisible = () => {
    const visibleIds = displayedCustomers.map(c => c.id);
    setSelectedCustomers(new Set(visibleIds));
  };

  const clearSelection = () => {
    setSelectedCustomers(new Set());
    setSelectionMode(false);
  };

  const toggleSelectionMode = () => {
    if (selectionMode) {
      // Turning off - clear selection
      setSelectedCustomers(new Set());
    }
    setSelectionMode(!selectionMode);
  };

  const handleBulkDelete = async () => {
    if (selectedCustomers.size === 0) return;
    
    setBulkDeleting(true);
    let deleted = 0;
    let failed = 0;
    
    try {
      for (const customerId of selectedCustomers) {
        try {
          await customerAPI.delete(customerId);
          deleted++;
        } catch (error) {
          failed++;
        }
      }
      
      if (deleted > 0) {
        toast.success(`${deleted} klant(en) verwijderd`);
      }
      if (failed > 0) {
        toast.error(`${failed} klant(en) konden niet worden verwijderd`);
      }
      
      setSelectedCustomers(new Set());
      setBulkDeleteConfirmOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleExportSelected = () => {
    const selectedData = customers.filter(c => selectedCustomers.has(c.id));
    
    if (selectedData.length === 0) {
      toast.error('Geen klanten geselecteerd');
      return;
    }
    
    // Create CSV content
    const headers = ['Voornaam', 'Achternaam', 'Email', 'Telefoon', 'Bedrijf', 'BTW Nummer', 'Taal', 'Notities'];
    const rows = selectedData.map(c => [
      c.first_name || '',
      c.last_name || '',
      c.email || '',
      c.phone || '',
      c.company_name || '',
      c.vat_number || '',
      c.language || '',
      (c.notes || '').replace(/"/g, '""')
    ]);
    
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');
    
    // Download file
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `klanten_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success(`${selectedData.length} klant(en) geëxporteerd`);
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in max-w-full overflow-x-hidden px-2 sm:px-0" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-4xl font-bold tracking-tight">Klanten</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filteredCustomers.length} van {customers.length} klanten
            {selectedCustomers.size > 0 && (
              <span className="ml-2 text-primary font-medium">
                ({selectedCustomers.size} geselecteerd)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Selection mode actions */}
          {selectionMode && selectedCustomers.size > 0 && hasRole('admin') && (
            <>
              <Button 
                variant="outline" 
                onClick={handleExportSelected}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                Exporteer ({selectedCustomers.size})
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => setBulkDeleteConfirmOpen(true)}
                className="gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Verwijder ({selectedCustomers.size})
              </Button>
            </>
          )}
          {/* Selection mode toggle */}
          {hasRole('admin') && (
            <Button 
              variant={selectionMode ? "secondary" : "outline"}
              onClick={toggleSelectionMode}
              className="gap-2"
            >
              <CheckSquare className="w-4 h-4" />
              {selectionMode ? 'Annuleren' : 'Selecteren'}
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-customer-btn">
                <Plus className="w-4 h-4 mr-2" />
                Klant Toevoegen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nieuwe Klant</DialogTitle>
                <DialogDescription>Vul de klantgegevens in</DialogDescription>
              </DialogHeader>
              <CustomerForm
                onSubmit={handleCreate}
                onCancel={() => setDialogOpen(false)}
                loading={saving}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Bulk Selection Bar - only visible in selection mode */}
      {selectionMode && hasRole('admin') && (
        <div className="flex items-center gap-3 p-3 bg-primary/10 rounded-lg border border-primary/30">
          <Checkbox
            checked={selectedCustomers.size > 0 && selectedCustomers.size === displayedCustomers.length}
            onCheckedChange={(checked) => {
              if (checked) {
                selectAllVisible();
              } else {
                setSelectedCustomers(new Set());
              }
            }}
          />
          <span className="text-sm">
            {selectedCustomers.size === 0 
              ? `Selecteer klanten om te verwijderen of exporteren`
              : `${selectedCustomers.size} klant(en) geselecteerd`
            }
          </span>
          {selectedCustomers.size > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setSelectedCustomers(new Set())}>
              <X className="w-3 h-3 mr-1" />
              Deselecteer
            </Button>
          )}
          {selectedCustomers.size < displayedCustomers.length && (
            <Button variant="ghost" size="sm" onClick={selectAllVisible}>
              Selecteer alle ({displayedCustomers.length})
            </Button>
          )}
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Zoeken op naam, email, telefoon, bedrijf..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="customer-search"
          />
        </div>
        
        <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="filter-btn">
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-1 bg-primary text-primary-foreground">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-md overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center justify-between">
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={resetFilters} className="text-muted-foreground">
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Reset
                  </Button>
                )}
              </SheetTitle>
              <SheetDescription>
                Filter klanten op verschillende criteria
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-2">
              {/* Installaties Filter */}
              <FilterSection title="Installaties" icon={Sun} defaultOpen={true}>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Zonnepanelen</Label>
                    <div className="flex gap-2">
                      <Button 
                        variant={filters.hasSolarPanels === true ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasSolarPanels: prev.hasSolarPanels === true ? null : true }))}
                        className="flex-1"
                      >
                        <Sun className="w-3 h-3 mr-1" />
                        Heeft
                      </Button>
                      <Button 
                        variant={filters.hasSolarPanels === false ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasSolarPanels: prev.hasSolarPanels === false ? null : false }))}
                        className="flex-1"
                      >
                        Geen
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Batterij</Label>
                    <div className="flex gap-2">
                      <Button 
                        variant={filters.hasBattery === true ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasBattery: prev.hasBattery === true ? null : true }))}
                        className="flex-1"
                      >
                        <Battery className="w-3 h-3 mr-1" />
                        Heeft
                      </Button>
                      <Button 
                        variant={filters.hasBattery === false ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasBattery: prev.hasBattery === false ? null : false }))}
                        className="flex-1"
                      >
                        Geen
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Laadpaal</Label>
                    <div className="flex gap-2">
                      <Button 
                        variant={filters.hasEVCharger === true ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasEVCharger: prev.hasEVCharger === true ? null : true }))}
                        className="flex-1"
                      >
                        <Zap className="w-3 h-3 mr-1" />
                        Heeft
                      </Button>
                      <Button 
                        variant={filters.hasEVCharger === false ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasEVCharger: prev.hasEVCharger === false ? null : false }))}
                        className="flex-1"
                      >
                        Geen
                      </Button>
                    </div>
                  </div>
                </div>
              </FilterSection>

              {/* Aantal Panelen Filter */}
              <FilterSection title="Aantal Zonnepanelen" icon={Sun}>
                <div className="space-y-4 pt-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>{filters.minPanels} panelen</span>
                    <span>{filters.maxPanels}+ panelen</span>
                  </div>
                  <div className="px-1">
                    <Slider
                      value={[filters.minPanels, filters.maxPanels]}
                      min={0}
                      max={100}
                      step={5}
                      onValueChange={([min, max]) => setFilters(prev => ({ ...prev, minPanels: min, maxPanels: max }))}
                      className="w-full"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setFilters(prev => ({ ...prev, minPanels: 0, maxPanels: 10 }))}
                      className="text-xs"
                    >
                      0-10
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setFilters(prev => ({ ...prev, minPanels: 10, maxPanels: 20 }))}
                      className="text-xs"
                    >
                      10-20
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setFilters(prev => ({ ...prev, minPanels: 20, maxPanels: 100 }))}
                      className="text-xs"
                    >
                      20+
                    </Button>
                  </div>
                </div>
              </FilterSection>

              {/* Gemeente Filter */}
              <FilterSection title="Gemeente" icon={MapPin}>
                <div className="space-y-2 pt-2">
                  <Input
                    placeholder="Zoek gemeente..."
                    className="h-8 text-sm"
                    onChange={(e) => {
                      // Filter cities in the list
                      const searchInput = e.target;
                      const items = searchInput.parentElement.querySelectorAll('[data-city]');
                      items.forEach(item => {
                        const city = item.getAttribute('data-city').toLowerCase();
                        item.style.display = city.includes(e.target.value.toLowerCase()) ? '' : 'none';
                      });
                    }}
                  />
                  <div className="max-h-48 overflow-y-auto space-y-1 pr-2">
                    {filterOptions.cities.map(city => (
                      <div 
                        key={city} 
                        data-city={city}
                        className="flex items-center space-x-2 py-1"
                      >
                        <Checkbox
                          id={`city-${city}`}
                          checked={filters.cities.includes(city)}
                          onCheckedChange={() => toggleArrayFilter('cities', city)}
                        />
                        <label 
                          htmlFor={`city-${city}`}
                          className="text-sm cursor-pointer flex-1 truncate"
                        >
                          {city}
                        </label>
                      </div>
                    ))}
                  </div>
                  {filters.cities.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-2 border-t">
                      {filters.cities.map(city => (
                        <Badge 
                          key={city} 
                          variant="secondary" 
                          className="text-xs cursor-pointer"
                          onClick={() => toggleArrayFilter('cities', city)}
                        >
                          {city}
                          <X className="w-3 h-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </FilterSection>

              {/* Postcode Filter */}
              <FilterSection title="Postcode" icon={MapPin}>
                <div className="space-y-2 pt-2">
                  <Input
                    placeholder="Zoek postcode..."
                    className="h-8 text-sm"
                    onChange={(e) => {
                      // Filter postal codes in the list
                      const searchInput = e.target;
                      const items = searchInput.parentElement.querySelectorAll('[data-postalcode]');
                      items.forEach(item => {
                        const postalCode = item.getAttribute('data-postalcode').toLowerCase();
                        item.style.display = postalCode.includes(e.target.value.toLowerCase()) ? '' : 'none';
                      });
                    }}
                  />
                  <div className="max-h-48 overflow-y-auto space-y-1 pr-2">
                    {filterOptions.postalCodes.map(postalCode => (
                      <div 
                        key={postalCode} 
                        data-postalcode={postalCode}
                        className="flex items-center space-x-2 py-1"
                      >
                        <Checkbox
                          id={`postalcode-${postalCode}`}
                          checked={filters.postalCodes.includes(postalCode)}
                          onCheckedChange={() => toggleArrayFilter('postalCodes', postalCode)}
                        />
                        <label 
                          htmlFor={`postalcode-${postalCode}`}
                          className="text-sm cursor-pointer flex-1 truncate"
                        >
                          {postalCode}
                        </label>
                      </div>
                    ))}
                  </div>
                  {filters.postalCodes.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-2 border-t">
                      {filters.postalCodes.map(postalCode => (
                        <Badge 
                          key={postalCode} 
                          variant="secondary" 
                          className="text-xs cursor-pointer"
                          onClick={() => toggleArrayFilter('postalCodes', postalCode)}
                        >
                          {postalCode}
                          <X className="w-3 h-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </FilterSection>

              {/* Taal Filter */}
              <FilterSection title="Taal" icon={Building2}>
                <div className="space-y-2 pt-2">
                  {[
                    { value: 'NL', label: 'Nederlands' },
                    { value: 'FR', label: 'Frans' },
                    { value: 'EN', label: 'Engels' },
                    { value: 'DE', label: 'Duits' }
                  ].map(lang => (
                    <div key={lang.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`lang-${lang.value}`}
                        checked={filters.languages.includes(lang.value)}
                        onCheckedChange={() => toggleArrayFilter('languages', lang.value)}
                      />
                      <label 
                        htmlFor={`lang-${lang.value}`}
                        className="text-sm cursor-pointer"
                      >
                        {lang.label}
                      </label>
                    </div>
                  ))}
                </div>
              </FilterSection>

              {/* Bedrijf Filter */}
              <FilterSection title="Klanttype" icon={Building2}>
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant={filters.hasCompany === true ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, hasCompany: prev.hasCompany === true ? null : true }))}
                    className="flex-1"
                  >
                    <Building2 className="w-3 h-3 mr-1" />
                    Zakelijk
                  </Button>
                  <Button 
                    variant={filters.hasCompany === false ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, hasCompany: prev.hasCompany === false ? null : false }))}
                    className="flex-1"
                  >
                    Particulier
                  </Button>
                </div>
              </FilterSection>

              {/* Reinigingscontract Filter */}
              <FilterSection title="Reinigingscontract" icon={Sparkles}>
                <div className="space-y-2 pt-2">
                  <div className="flex gap-2">
                    <Button 
                      variant={filters.cleaningContract === '2_yearly' ? "default" : "outline"} 
                      size="sm"
                      onClick={() => setFilters(prev => ({ ...prev, cleaningContract: prev.cleaningContract === '2_yearly' ? null : '2_yearly' }))}
                      className="flex-1"
                    >
                      <Sparkles className="w-3 h-3 mr-1 text-amber-500" />
                      2-jaarlijks
                    </Button>
                    <Button 
                      variant={filters.cleaningContract === '3_yearly' ? "default" : "outline"} 
                      size="sm"
                      onClick={() => setFilters(prev => ({ ...prev, cleaningContract: prev.cleaningContract === '3_yearly' ? null : '3_yearly' }))}
                      className="flex-1"
                    >
                      <Sparkles className="w-3 h-3 mr-1 text-blue-500" />
                      3-jaarlijks
                    </Button>
                  </div>
                  <Button 
                    variant={filters.cleaningContract === 'none' ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, cleaningContract: prev.cleaningContract === 'none' ? null : 'none' }))}
                    className="w-full"
                  >
                    Geen contract
                  </Button>
                </div>
              </FilterSection>

              {/* Installatie Datum Filter */}
              <FilterSection title="Installatie Datum" icon={CalendarIcon}>
                <div className="space-y-3 pt-2">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Van</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal h-9">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {filters.installationDateFrom ? format(parse(filters.installationDateFrom, 'yyyy-MM-dd', new Date()), 'd MMM yyyy', { locale: nl }) : 'Selecteer'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={filters.installationDateFrom ? parse(filters.installationDateFrom, 'yyyy-MM-dd', new Date()) : undefined}
                          onSelect={(date) => date && setFilters(prev => ({ ...prev, installationDateFrom: format(date, 'yyyy-MM-dd') }))}
                          locale={nl}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Tot</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal h-9">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {filters.installationDateTo ? format(parse(filters.installationDateTo, 'yyyy-MM-dd', new Date()), 'd MMM yyyy', { locale: nl }) : 'Selecteer'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={filters.installationDateTo ? parse(filters.installationDateTo, 'yyyy-MM-dd', new Date()) : undefined}
                          onSelect={(date) => date && setFilters(prev => ({ ...prev, installationDateTo: format(date, 'yyyy-MM-dd') }))}
                          locale={nl}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  {(filters.installationDateFrom || filters.installationDateTo) && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setFilters(prev => ({ ...prev, installationDateFrom: '', installationDateTo: '' }))}
                      className="w-full text-xs"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Wis datumfilter
                    </Button>
                  )}
                </div>
              </FilterSection>

              {/* Gekochte Producten Filter (Targeted Marketing) */}
              {filterOptions.purchasedProducts.length > 0 && (
                <FilterSection title="Gekochte Producten" icon={ShoppingBag}>
                  <div className="space-y-2 pt-2 max-h-48 overflow-y-auto">
                    {filterOptions.purchasedProducts.map(product => (
                      <div key={product} className="flex items-center space-x-2">
                        <Checkbox
                          id={`product-${product}`}
                          checked={filters.purchasedProducts?.includes(product)}
                          onCheckedChange={(checked) => {
                            setFilters(prev => ({
                              ...prev,
                              purchasedProducts: checked 
                                ? [...(prev.purchasedProducts || []), product]
                                : (prev.purchasedProducts || []).filter(p => p !== product)
                            }));
                          }}
                        />
                        <Label 
                          htmlFor={`product-${product}`} 
                          className="text-sm cursor-pointer flex-1 truncate"
                          title={product}
                        >
                          {product}
                        </Label>
                      </div>
                    ))}
                    {filters.purchasedProducts?.length > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, purchasedProducts: [] }))}
                        className="w-full text-xs mt-2"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Wis productfilter
                      </Button>
                    )}
                  </div>
                </FilterSection>
              )}

              {/* Sortering */}
              <FilterSection title="Sortering" icon={Filter}>
                <div className="space-y-3 pt-2">
                  <Select 
                    value={filters.sortBy} 
                    onValueChange={(v) => setFilters(prev => ({ ...prev, sortBy: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Sorteer op..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Naam</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="company">Bedrijf</SelectItem>
                      <SelectItem value="panels">Aantal panelen</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button 
                      variant={filters.sortOrder === 'asc' ? "default" : "outline"} 
                      size="sm"
                      onClick={() => setFilters(prev => ({ ...prev, sortOrder: 'asc' }))}
                      className="flex-1"
                    >
                      Oplopend
                    </Button>
                    <Button 
                      variant={filters.sortOrder === 'desc' ? "default" : "outline"} 
                      size="sm"
                      onClick={() => setFilters(prev => ({ ...prev, sortOrder: 'desc' }))}
                      className="flex-1"
                    >
                      Aflopend
                    </Button>
                  </div>
                </div>
              </FilterSection>
            </div>

            <SheetFooter className="mt-6">
              <Button onClick={() => setFilterSheetOpen(false)} className="w-full">
                Toon {filteredCustomers.length} resultaten
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Actieve filters:</span>
          {filters.hasSolarPanels !== null && (
            <Badge variant="secondary" className="cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, hasSolarPanels: null }))}>
              {filters.hasSolarPanels ? 'Heeft zonnepanelen' : 'Geen zonnepanelen'}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
          {filters.hasBattery !== null && (
            <Badge variant="secondary" className="cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, hasBattery: null }))}>
              {filters.hasBattery ? 'Heeft batterij' : 'Geen batterij'}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
          {filters.hasEVCharger !== null && (
            <Badge variant="secondary" className="cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, hasEVCharger: null }))}>
              {filters.hasEVCharger ? 'Heeft laadpaal' : 'Geen laadpaal'}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
          {(filters.minPanels > 0 || filters.maxPanels < 100) && (
            <Badge variant="secondary" className="cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, minPanels: 0, maxPanels: 100 }))}>
              {filters.minPanels}-{filters.maxPanels} panelen
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
          {filters.languages.length > 0 && (
            <Badge variant="secondary" className="cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, languages: [] }))}>
              {filters.languages.join(', ')}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
          {filters.cities.length > 0 && (
            <Badge variant="secondary" className="cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, cities: [] }))}>
              {filters.cities.length} gemeente(n)
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
          {filters.postalCodes.length > 0 && (
            <Badge variant="secondary" className="cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, postalCodes: [] }))}>
              {filters.postalCodes.length} postcode(s)
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
          {filters.hasCompany !== null && (
            <Badge variant="secondary" className="cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, hasCompany: null }))}>
              {filters.hasCompany ? 'Zakelijk' : 'Particulier'}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
          <Button variant="ghost" size="sm" onClick={resetFilters} className="text-xs">
            Wis alle
          </Button>
        </div>
      )}

      {/* Customer List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="h-40 animate-pulse bg-muted" />
          ))}
        </div>
      ) : filteredCustomers.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="text-muted-foreground">
            <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">Geen klanten gevonden</p>
            <p className="text-sm mt-1">Pas je filters aan of voeg een nieuwe klant toe</p>
            {activeFilterCount > 0 && (
              <Button variant="outline" className="mt-4" onClick={resetFilters}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset filters
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCustomers.slice(0, displayLimit).map((customer) => {
            const panelCount = filterOptions.customerPanelCounts[customer.id] || 0;
            const customerProperties = properties.filter(p => p.customer_id === customer.id);
            const propertyIds = customerProperties.map(p => p.id);
            const customerInstallations = installations.filter(i => propertyIds.includes(i.property_id));
            const hasBattery = customerInstallations.some(i => i.installation_type === 'battery');
            const hasEV = customerInstallations.some(i => i.installation_type === 'ev_charger');
            const showCompanyFirst = customer.company_name &&
              customer.company_name.trim() !== '' &&
              customer.company_name.toLowerCase() !== 'onbekend';
            const contactName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
            const copyInfo = (text, label) => {
              if (!text) { toast.error(`Geen ${label.toLowerCase()} beschikbaar`); return; }
              navigator.clipboard.writeText(text);
              toast.success(`${label} gekopieerd`);
            };
            const addressText = customerProperties
              .map(p => `${(p.street || '')} ${(p.number || '')}`.trim() + `, ${(p.postal_code || '')} ${(p.city || '')}`.trim())
              .map(s => s.replace(/^,\s*/, '').replace(/,\s*$/, ''))
              .filter(s => s.length > 2)
              .join('\n');
            
            return (
              <Card 
                key={customer.id} 
                className={`card-hover cursor-pointer ${selectionMode && selectedCustomers.has(customer.id) ? 'ring-2 ring-primary' : ''}`}
                onClick={() => {
                  if (selectionMode) {
                    toggleCustomerSelection(customer.id);
                  } else {
                    navigate(`/customers/${customer.id}`);
                  }
                }}
                data-testid={`customer-card-${customer.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {selectionMode && (
                        <Checkbox
                          checked={selectedCustomers.has(customer.id)}
                          onCheckedChange={() => toggleCustomerSelection(customer.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1"
                        />
                      )}
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary">
                          {customer.first_name?.[0]}{customer.last_name?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium" data-testid={`customer-card-name-${customer.id}`}>
                          {showCompanyFirst ? customer.company_name : contactName}
                        </p>
                        {showCompanyFirst && contactName && (
                          <p className="text-xs text-muted-foreground">{contactName}</p>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`customer-menu-${customer.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/customers/${customer.id}`); }}>
                          <Eye className="w-4 h-4 mr-2" />
                          Bekijken
                        </DropdownMenuItem>
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger onClick={(e) => e.stopPropagation()} data-testid={`customer-copy-menu-${customer.id}`}>
                            <Copy className="w-4 h-4 mr-2" />
                            Gegevens kopiëren
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            <DropdownMenuItem data-testid={`copy-name-${customer.id}`} onClick={(e) => { e.stopPropagation(); copyInfo(showCompanyFirst ? `${customer.company_name}${contactName ? ` - ${contactName}` : ''}` : contactName, 'Naam'); }}>
                              Naam
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid={`copy-phone-${customer.id}`} onClick={(e) => { e.stopPropagation(); copyInfo(formatPhoneNumber(customer.phone), 'Telefoon'); }}>
                              Telefoon
                            </DropdownMenuItem>
                            {customerProperties.length > 1 ? (
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger onClick={(e) => e.stopPropagation()} data-testid={`copy-address-menu-${customer.id}`}>
                                  Adressen
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  {customerProperties.map((p) => {
                                    const addr = `${(p.street || '')} ${(p.number || '')}`.trim() + `, ${(p.postal_code || '')} ${(p.city || '')}`.trim();
                                    const cleanAddr = addr.replace(/^,\s*/, '').replace(/,\s*$/, '');
                                    return (
                                      <DropdownMenuItem key={p.id} data-testid={`copy-address-${p.id}`} onClick={(e) => { e.stopPropagation(); copyInfo(cleanAddr, 'Adres'); }}>
                                        {cleanAddr || 'Onbekend adres'}
                                      </DropdownMenuItem>
                                    );
                                  })}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            ) : (
                              <DropdownMenuItem data-testid={`copy-address-${customer.id}`} onClick={(e) => { e.stopPropagation(); copyInfo(addressText, 'Adres'); }}>
                                Adres
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem data-testid={`copy-email-${customer.id}`} onClick={(e) => { e.stopPropagation(); copyInfo(customer.email, 'E-mailadres'); }}>
                              E-mailadres
                            </DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        {hasRole(['admin', 'sales']) && (
                          <>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditCustomer(customer); }}>
                              <Edit className="w-4 h-4 mr-2" />
                              Bewerken
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openCameraDialog(customer); }}>
                              <Camera className="w-4 h-4 mr-2" />
                              Foto's
                            </DropdownMenuItem>
                          </>
                        )}
                        {hasRole('admin') && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); handleDelete(customer.id); }}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Verwijderen
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="mt-4 space-y-2">
                    <div 
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{customer.email}</span>
                    </div>
                    <a 
                      href={`tel:${customer.phone}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-green-400 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      <span>{formatPhoneNumber(customer.phone)}</span>
                    </a>
                  </div>

                  {/* Installation badges */}
                  <div className="mt-3 flex flex-wrap gap-1">
                    {panelCount > 0 && (
                      <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/30">
                        <Sun className="w-3 h-3 mr-1" />
                        {panelCount} panelen
                      </Badge>
                    )}
                    {hasBattery && (
                      <Badge variant="outline" className="text-xs bg-green-500/10 text-green-400 border-green-500/30">
                        <Battery className="w-3 h-3 mr-1" />
                        Batterij
                      </Badge>
                    )}
                    {hasEV && (
                      <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/30">
                        <Zap className="w-3 h-3 mr-1" />
                        Laadpaal
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Load More Button */}
        {filteredCustomers.length > displayLimit && (
          <div className="flex justify-center mt-6">
            <Button 
              variant="outline" 
              onClick={() => setDisplayLimit(prev => prev + 50)}
            >
              Meer laden ({filteredCustomers.length - displayLimit} resterend)
            </Button>
          </div>
        )}
      </>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editCustomer} onOpenChange={() => setEditCustomer(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Klant Bewerken</DialogTitle>
            <DialogDescription>Pas de klantgegevens aan</DialogDescription>
          </DialogHeader>
          {editCustomer && (
            <CustomerForm
              customer={editCustomer}
              onSubmit={handleUpdate}
              onCancel={() => setEditCustomer(null)}
              loading={saving}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Klant verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je {customerToDelete?.name} wilt verwijderen? 
              Alle bijbehorende adressen en installaties worden ook verwijderd. 
              Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCustomerToDelete(null)}>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-customer-btn"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Meerdere klanten verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Je staat op het punt om <span className="font-bold text-destructive">{selectedCustomers.size} klant(en)</span> te verwijderen. 
              Deze actie kan niet ongedaan worden gemaakt. Alle gekoppelde adressen en installaties blijven behouden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={bulkDeleting}
            >
              {bulkDeleting ? 'Bezig met verwijderen...' : `Verwijder ${selectedCustomers.size} klant(en)`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Camera/Photo Dialog */}
      <Dialog open={cameraDialogOpen} onOpenChange={setCameraDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Foto's - {cameraCustomer?.first_name} {cameraCustomer?.last_name}
            </DialogTitle>
            <DialogDescription>
              Maak een foto of bekijk bestaande foto's van deze klant
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Hidden file input for camera */}
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              capture="environment"
              onChange={handlePhotoCapture}
              className="hidden"
            />
            
            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={photoUploading}
                className="flex-1"
              >
                {photoUploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 mr-2" />
                )}
                {photoUploading ? 'Uploaden...' : 'Nieuwe Foto'}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setViewingPhotos(!viewingPhotos)}
                className="flex-1"
              >
                <Image className="w-4 h-4 mr-2" />
                {viewingPhotos ? 'Verberg Foto\'s' : `Bekijk Foto's (${customerPhotos.length})`}
              </Button>
            </div>
            
            {/* Photos grid */}
            {viewingPhotos && (
              <div className="space-y-3">
                {customerPhotos.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nog geen foto's voor deze klant
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {customerPhotos.map((photo) => (
                      <div key={photo.id} className="relative group rounded-lg overflow-hidden border">
                        <img
                          src={photo.file_url || `/api/documents/${photo.id}/download`}
                          alt={photo.description || 'Klant foto'}
                          className="w-full h-32 object-cover"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <a
                            href={photo.file_url || `/api/documents/${photo.id}/download`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white rounded-full"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => deletePhoto(photo.id)}
                            className="p-2 bg-red-500 rounded-full text-white"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-xs p-2 truncate">{photo.description || 'Foto'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCameraDialogOpen(false)}>
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
